/**
 * FounderSignBar — fixed top-right pill showing JWT status and one-click signing.
 *
 * Renders only when the signed-in user is on the ADMIN_EMAILS list.
 * Shows JWT expiry status with colour coding, and a dropdown with:
 *   - Copy JWT to clipboard
 *   - Refresh JWT
 *   - Show/hide raw JWT
 *   - Sign any SHA-256 hash with the server-side founder warm key
 *
 * Keyboard shortcut: Ctrl+Shift+S (Cmd+Shift+S on Mac) opens the sign panel.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { Session } from '@supabase/supabase-js';

import { getSupabase, isAdminEmail, isSupabaseConfigured, subscribeAuthChange } from '@/api/supabase';
import { useToast } from '@/components/Toaster';
import { signHashWithFounderKey } from '@/api/real/founderSign';

// ── JWT status ────────────────────────────────────────────────────────────────

type JwtStatus = 'ok' | 'expiring' | 'expired' | 'none';

function jwtStatus(session: Session | null): JwtStatus {
  if (!session) return 'none';
  const expiresAt = session.expires_at; // unix seconds
  if (!expiresAt) return 'none';
  const secsLeft = expiresAt - Math.floor(Date.now() / 1000);
  if (secsLeft <= 0) return 'expired';
  if (secsLeft < 600) return 'expiring';
  return 'ok';
}

function statusColor(s: JwtStatus): string {
  if (s === 'ok') return '#16a34a';
  if (s === 'expiring') return '#d97706';
  return '#dc2626';
}

function statusLabel(s: JwtStatus, session: Session | null): string {
  if (s === 'none' || s === 'expired') return 'Re-login needed';
  if (s === 'expiring') {
    const secsLeft = (session!.expires_at ?? 0) - Math.floor(Date.now() / 1000);
    const mins = Math.ceil(secsLeft / 60);
    return `JWT expires in ${mins}m`;
  }
  return 'Founder ready';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function FounderSignBar() {
  const [session, setSession] = useState<Session | null>(null);
  const [open, setOpen] = useState(false);
  const [showJwt, setShowJwt] = useState(false);
  const [signInput, setSignInput] = useState('');
  const [signResult, setSignResult] = useState('');
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toast = useToast();

  // Load session on mount and subscribe to changes.
  // Guard: getSupabase() throws when VITE_SUPABASE_URL is not set (mock-mode dev / CI).
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    void getSupabase().auth.getSession().then(({ data }) => setSession(data.session));
    return subscribeAuthChange(setSession);
  }, []);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Keyboard shortcut: Ctrl/Cmd + Shift + S.
  const openSign = useCallback(() => {
    setOpen(true);
    setSignInput('');
    setSignResult('');
    setSignError('');
  }, []);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'S') {
        e.preventDefault();
        openSign();
      }
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [openSign]);

  // Only render for founders.
  const email = session?.user?.email;
  if (!isAdminEmail(email)) return null;

  const status = jwtStatus(session);
  const color = statusColor(status);
  const label = statusLabel(status, session);

  async function handleCopyJwt() {
    if (!session?.access_token) return;
    await navigator.clipboard.writeText(session.access_token);
    toast.success('Copied', 'JWT copied to clipboard');
    setOpen(false);
  }

  async function handleRefresh() {
    const { error } = await getSupabase().auth.refreshSession();
    if (error) {
      toast.error('Refresh failed', error.message);
    } else {
      toast.success('Refreshed', 'JWT token refreshed');
    }
  }

  async function handleSign() {
    if (!signInput.trim()) return;
    setSigning(true);
    setSignError('');
    setSignResult('');
    try {
      const sig = await signHashWithFounderKey(signInput.trim());
      setSignResult(sig);
      await navigator.clipboard.writeText(sig);
      toast.success('Signed', 'Signature copied to clipboard');
    } catch (err) {
      setSignError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSigning(false);
    }
  }

  return (
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: 8,
        right: 16,
        zIndex: 9999,
      }}
    >
      {/* Pill trigger */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          height: 28,
          padding: '0 10px',
          borderRadius: 14,
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(15,15,15,0.85)',
          backdropFilter: 'blur(8px)',
          cursor: 'pointer',
          fontSize: 11,
          fontWeight: 500,
          color: '#e5e5e5',
          whiteSpace: 'nowrap',
        }}
        title="Founder signing controls (Ctrl+Shift+S)"
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: color,
            flexShrink: 0,
          }}
        />
        <span className="hidden sm:inline">{label}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 36,
            right: 0,
            width: 320,
            background: '#111',
            border: '1px solid #333',
            borderRadius: 10,
            boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
            padding: '12px 0',
            fontSize: 13,
          }}
        >
          {/* Actions row */}
          <div style={{ display: 'flex', gap: 4, padding: '0 12px 10px' }}>
            <DropBtn onClick={handleCopyJwt}>Copy JWT</DropBtn>
            <DropBtn onClick={handleRefresh}>Refresh JWT</DropBtn>
            <DropBtn onClick={() => setShowJwt((v) => !v)}>
              {showJwt ? 'Hide JWT' : 'Show JWT'}
            </DropBtn>
          </div>

          {/* Raw JWT display */}
          {showJwt && session?.access_token && (
            <div style={{ padding: '0 12px 10px' }}>
              <textarea
                readOnly
                value={session.access_token}
                rows={4}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: 10,
                  fontFamily: 'monospace',
                  background: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: 6,
                  color: '#9ca3af',
                  padding: 8,
                  resize: 'none',
                }}
              />
            </div>
          )}

          {/* Divider */}
          <div style={{ borderTop: '1px solid #222', margin: '4px 0 10px' }} />

          {/* Sign hash tool */}
          <div style={{ padding: '0 12px' }}>
            <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6b7280', marginBottom: 6 }}>
              Sign hash
            </div>
            <input
              type="text"
              value={signInput}
              onChange={(e) => setSignInput(e.target.value)}
              placeholder="sha256:1f7caa3b... or 64 hex chars"
              onKeyDown={(e) => { if (e.key === 'Enter') void handleSign(); }}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: 6,
                color: '#e5e5e5',
                fontFamily: 'monospace',
                fontSize: 11,
                padding: '6px 8px',
                marginBottom: 6,
                outline: 'none',
              }}
            />
            <button
              type="button"
              disabled={!signInput.trim() || signing}
              onClick={() => void handleSign()}
              style={{
                width: '100%',
                padding: '6px 0',
                background: signing ? '#374151' : '#6366f1',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: signInput.trim() && !signing ? 'pointer' : 'not-allowed',
                opacity: signInput.trim() && !signing ? 1 : 0.6,
              }}
            >
              {signing ? 'Signing…' : 'Sign with founder key'}
            </button>
            {signError && (
              <p style={{ color: '#f87171', fontSize: 11, marginTop: 6 }}>{signError}</p>
            )}
            {signResult && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 4 }}>Signature (copied to clipboard)</div>
                <div
                  style={{
                    fontFamily: 'monospace',
                    fontSize: 10,
                    wordBreak: 'break-all',
                    color: '#4ade80',
                    background: '#0a1a0a',
                    border: '1px solid #16a34a33',
                    borderRadius: 6,
                    padding: '6px 8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => void navigator.clipboard.writeText(signResult)}
                  title="Click to copy again"
                >
                  {signResult}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DropBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: '5px 0',
        background: '#1f1f1f',
        border: '1px solid #333',
        borderRadius: 6,
        color: '#d1d5db',
        fontSize: 11,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
